import { type AccessTarget, can, canAnywhere, canCompanyWide, type MeResponse } from '@epc/contracts';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<MeResponse | null> => {
    try {
      return await api<MeResponse>('/auth/me');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  staleTime: 60_000,
  retry: false,
});

export function useMe(): MeResponse {
  const { data } = useQuery(meQuery);
  if (!data) throw new Error('useMe() outside of an authenticated route');
  return data;
}

/** Демо-компания: статус разработки и ещё не сделанные функции посетителям не показываются */
export function useIsDemo(): boolean {
  const { data } = useQuery(meQuery);
  return Boolean(data?.tenant?.isDemo);
}

export function useCan(permission: string, target?: AccessTarget): boolean {
  const { data } = useQuery(meQuery);
  return target ? can(data?.access, permission, target) : canAnywhere(data?.access, permission);
}

/** Право на уровне компании или юрлица — для общих справочников группы, как проверяет API */
export function useCompanyWide(...permissions: string[]): boolean {
  const { data } = useQuery(meQuery);
  return permissions.some((p) => canCompanyWide(data?.access, p));
}

export function useSetMe() {
  const qc = useQueryClient();
  return (me: MeResponse | null) => qc.setQueryData(meQuery.queryKey, me);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}
